import * as Sentry from '@sentry/browser';
import { getSentryDSN, getTenantName, getEnvironment } from '../../constants';
import {
  getAppVersion,
  getDist,
  getSentryReleaseId,
} from '../../constants/globals';
import { getAccountStore, getDeviceAndUserStore } from '../../storage/stores';
import { APIError } from '../errors/api/APIError';
import { FetchTimeoutError } from '../errors/api/FetchTimeoutError';
import { OnBoardingError } from '../errors/OnBoardingError';

declare const isDelegation: boolean;

export const JWT_TOKEN_TAG = 'device.jwt';

// Add here the error classes that should not be reported.
// Adding a base class will cause all errors extended from it to also be ignored.
const IGNORED_ERROR_CLASSES = [APIError, FetchTimeoutError, OnBoardingError];

/*
 * Initialize Sentry and set basic information (tenant, version and isDelegation)
 *
 * @param filterGlobalErrors - Whether to filter global errors or not.
 * We should out errors reported from the content_script because if a website also uses Sentry,
 * the extension might send events to the website's Sentry project, or vice versa.
 *
 * @remarks
 *
 * This will not identify the user nor the device, this is set later:
 * We want to start reporting events even before we initialize the stores to get those details,
 * as well as events reported before the user is authenticated or the device created.
 */
export const setupSentry = ({ filterGlobalErrors = false } = {}) => {
  const defaultIntegrations = Sentry.getDefaultIntegrations({});
  const integrations = filterGlobalErrors
    ? defaultIntegrations.filter(defaultIntegration => {
        return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
          defaultIntegration.name
        );
      })
    : defaultIntegrations;

  const client = new Sentry.BrowserClient({
    dsn: getSentryDSN(),
    transport: Sentry.makeFetchTransport,
    stackParser: Sentry.defaultStackParser,
    environment: getEnvironment(),
    integrations: integrations,
    tracesSampleRate: 1.0,
    release: getSentryReleaseId(),
    dist: getDist(),
    beforeSend: beforeSend,
  });
  client.init();

  Sentry.setCurrentClient(client);

  Sentry.setTag('tenant', getTenantName());
  Sentry.setTag('version', getAppVersion());
  Sentry.setTag('isDelegation', isDelegation);
};

/*
 * Set device and account UIDs for Sentry events
 */
export const identifySentry = () => {
  const deviceUID = getDeviceAndUserStore()?.get()?.device;
  if (deviceUID) {
    Sentry.setTag('device.uid', deviceUID);
  }

  const accountUID = getAccountStore()?.get()?.accountUid;
  if (accountUID) {
    Sentry.setUser({ id: accountUID });
  }
};

/*
 * Filter Sentry events before sending them
 */
const beforeSend = (event: Sentry.ErrorEvent, hint: Sentry.EventHint) => {
  const exception = hint?.originalException;
  if (exception) {
    for (const ErrorClass of IGNORED_ERROR_CLASSES) {
      if (exception instanceof ErrorClass) {
        return null; // Ignore this event type
      }
    }
  }
  return event;
};
